import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import pluralize from 'pluralize';

import { UTILITY_TENANT_BULK_IMPORT_SYNC_STATUS_FAILED } from 'common/utility/constants';

import {
  PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API,
  PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_APPFOLIO,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_BUILDIUM,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_PROPERTYWARE,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_RENTVINE,
} from 'common/propertyManagementSoftwareConstants';

import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrLink from 'client/components/SrLink/SrLink';

import './UPPropertyManagementSoftwareBanner.less';

const BANNER_STATUS_DISABLED = 'disabled';
const BANNER_STATUS_NOT_SYNCED = 'notSynced';
const BANNER_STATUS_SYNCED = 'synced';
const BANNER_STATUS_NOT_SYNCING = 'notSyncing';

const isPropertyManagementSoftwareDisabled = (pms) => {
  if (!pms) {
    return true;
  }

  const isApiTypeNotConnected =
    pms.enabled &&
    pms.integrationType === PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API &&
    !pms.connected;

  const isScraperTypeNotConnected =
    pms.integrationType === PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER &&
    pms.enabled &&
    !pms.connected;

  return isApiTypeNotConnected || isScraperTypeNotConnected;
};

const shouldHideBanner = (pms, lastSync) => {
  // TODO @Gustavo: Remove this when buildium syncs are supported.
  // pms.enabled should be true for all supported PMSs.
  const supportedPms = [
    PROPERTY_MANAGEMENT_SOFTWARE_NAME_BUILDIUM,
    PROPERTY_MANAGEMENT_SOFTWARE_NAME_RENTVINE,
    PROPERTY_MANAGEMENT_SOFTWARE_NAME_APPFOLIO,
    PROPERTY_MANAGEMENT_SOFTWARE_NAME_PROPERTYWARE,
  ];

  if (!supportedPms.includes(pms.name)) {
    return true;
  }

  return !pms.enabled && !lastSync;
};

const isSyncStale = (pms, lastSync) => {
  if (!lastSync) return false;

  const today = moment().startOf('day');
  const daysSinceLastSync = today.diff(lastSync.createdAt, 'days');
  const syncThresholdDays = pms.name === PROPERTY_MANAGEMENT_SOFTWARE_NAME_BUILDIUM ? 5 : 3;

  const isSyncTooOld = daysSinceLastSync >= syncThresholdDays;
  const hasSyncFailed = lastSync.status === UTILITY_TENANT_BULK_IMPORT_SYNC_STATUS_FAILED;

  return isSyncTooOld || hasSyncFailed;
};

const getBannerStatus = ({ utilityPartner }) => {
  const propertyManagementSoftware = utilityPartner.propertyManagementSoftware;
  const lastTenantBulkImportSync = utilityPartner.lastTenantBulkImportSync;

  if (isPropertyManagementSoftwareDisabled(propertyManagementSoftware)) {
    return BANNER_STATUS_DISABLED;
  }

  if (shouldHideBanner(propertyManagementSoftware, lastTenantBulkImportSync)) {
    return null;
  }

  if (!lastTenantBulkImportSync) {
    return BANNER_STATUS_NOT_SYNCED;
  }

  if (isSyncStale(propertyManagementSoftware, lastTenantBulkImportSync)) {
    return BANNER_STATUS_NOT_SYNCING;
  }

  return BANNER_STATUS_SYNCED;
};

const UPPropertyManagementSoftwareBanner = ({
  utilityPartner,
  onSelectPropertyManagementSoftware,
}) => {
  const { lastTenantBulkImportSync, propertyManagementSoftware } = utilityPartner;

  const bannerStatus = getBannerStatus({ utilityPartner });
  const lastTenantBulkImportSyncAt = lastTenantBulkImportSync
    ? moment(lastTenantBulkImportSync.createdAt).calendar(null, {
        sameDay: '[today] [at] h:mma',
        lastDay: '[yesterday] [at] h:mma',
        lastWeek: 'MMM D [at] h:mma',
        sameElse: 'MMM D [at] h:mma',
      })
    : null;

  if (bannerStatus === BANNER_STATUS_NOT_SYNCED) {
    return (
      <div styleName="banner notSynced">
        <div styleName="title">
          📅&nbsp;&nbsp;{propertyManagementSoftware.name} sync scheduled daily
          <span styleName="subtitle"> starting tomorrow around 9am.</span>
        </div>
        <div styleName="link" onClick={onSelectPropertyManagementSoftware}>
          Go to integration setup
        </div>
      </div>
    );
  }

  if (bannerStatus === BANNER_STATUS_SYNCED) {
    return (
      <div styleName="banner sync">
        <div styleName="title">
          ✅&nbsp;&nbsp;{propertyManagementSoftware.name} is synced successfully
          <span styleName="subtitle"> (last sync {lastTenantBulkImportSyncAt})</span>
        </div>
        <div />
      </div>
    );
  }

  if (bannerStatus === BANNER_STATUS_NOT_SYNCING) {
    const today = moment();
    const diffInDays = today.diff(lastTenantBulkImportSync.createdAt, 'days');
    const title =
      lastTenantBulkImportSync.status === UTILITY_TENANT_BULK_IMPORT_SYNC_STATUS_FAILED
        ? `❌  ${propertyManagementSoftware.name} has not synced!`
        : `❌  ${propertyManagementSoftware.name} has not synced for ${pluralize(
            'day',
            diffInDays,
            true,
          )}!`;
    const subtitle =
      lastTenantBulkImportSync.status === UTILITY_TENANT_BULK_IMPORT_SYNC_STATUS_FAILED
        ? ''
        : ` (last sync ${lastTenantBulkImportSyncAt})`;

    return (
      <div styleName="banner notSyncing">
        <div styleName="title">
          {title}
          <span styleName="subtitle">{subtitle}</span>
        </div>
        <SrLink styleName="link" to={utilityPartner?.accountManager?.hyperlinkUrl} target="_blank">
          See how to fix it
        </SrLink>
      </div>
    );
  }

  if (bannerStatus === BANNER_STATUS_DISABLED) {
    return (
      <div styleName="banner neverSynced">
        <div>
          <div styleName="title">🟠&nbsp;&nbsp;PM Software Sync</div>
          <div styleName="description">
            {'Property Managers who '}
            <SrLink styleName="descriptionLink" onClick={onSelectPropertyManagementSoftware}>
              sync their PM software
            </SrLink>
            {' get more tenant success.'}
            <br />
            Auto-reminders for tenants with links to setup utilities.
            <SrLink
              to="https://sunroomrentals.com/up-pm-sync-learn-more"
              target="_blank"
              styleName="link"
            >
              {' '}
              See how it works →
            </SrLink>
          </div>
          <div styleName="logoBox">
            <div styleName="logosImg" />
          </div>
        </div>
        <div styleName="ctaBox">
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            onClick={onSelectPropertyManagementSoftware}
            hasBoxShadow
          >
            Activate Sync
          </SrButton>
        </div>
      </div>
    );
  }

  return null;
};

UPPropertyManagementSoftwareBanner.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
};

UPPropertyManagementSoftwareBanner.displayName = 'UPPropertyManagementSoftwareBanner';

export default UPPropertyManagementSoftwareBanner;
